import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../components/Navbar';
import { saveTokens } from '../utils/auth';
import { ALLOWED_USERNAMES, API_ENDPOINTS, REPORT_API_ENDPOINTS } from '../config/api';
import { checkAccessWithToken } from '../utils/consoleAccess';

interface Credentials {
  PhoneOrUserName: string;
  Password: string;
}

const SAVED_CREDENTIALS_KEY = 'pingtop_saved_credentials';
const SAVED_ENV_KEY = 'pingtop_environment';

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState<Credentials>({ PhoneOrUserName: '', Password: '' });
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isProd, setIsProd] = useState<boolean>(() => {
    const saved = localStorage.getItem(SAVED_ENV_KEY);
    return saved !== 'dev'; // Default to prod
  });
  const navigate = useNavigate();

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem(SAVED_CREDENTIALS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCredentials(parsed);
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem(SAVED_CREDENTIALS_KEY);
      }
    }
  }, []);

  // Save environment preference
  const handleEnvToggle = () => {
    const newValue = !isProd;
    setIsProd(newValue);
    localStorage.setItem(SAVED_ENV_KEY, newValue ? 'prod' : 'dev');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); // Set loading to true when the form is submitted
    try {
      const baseUrl = isProd ? API_ENDPOINTS.prod : API_ENDPOINTS.dev;
      const response = await fetch(`${baseUrl}/auth/v1/loginV1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const data = await response.json();
      if (response.ok) {
        // Check if the user is in the allowed list
        // Use the username from the login response profile (works for both phone and username login)
        const profileUsername = (data.data?.profile?.UserName || credentials.PhoneOrUserName).toLowerCase().trim();
        if (!ALLOWED_USERNAMES.includes(profileUsername)) {
          toast.error('Access denied: You are not authorized to use this system.');
          return;
        }

        // The server's moderator list decides; nothing is stored until it says yes.
        const reportApiBaseUrl = isProd ? REPORT_API_ENDPOINTS.prod : REPORT_API_ENDPOINTS.dev;
        const access = await checkAccessWithToken(reportApiBaseUrl, data.data.token);
        if (access !== 'allowed') {
          toast.error(
            access === 'denied'
              ? 'Access denied: You are not authorized to use this system.'
              : 'Could not verify access. Please try again.'
          );
          return;
        }

        // Save or clear credentials based on remember me
        if (rememberMe) {
          localStorage.setItem(SAVED_CREDENTIALS_KEY, JSON.stringify(credentials));
        } else {
          localStorage.removeItem(SAVED_CREDENTIALS_KEY);
        }

        toast.success('Login Successful!');

        // Save both auth token and refresh token
        const authToken = data.data.token;
        const refreshToken = data.data?.refreshToken?.token || data.data?.refresh_token;

        if (refreshToken) {
          saveTokens(authToken, refreshToken);
        } else {
          // Fallback if refresh token not provided — save just the auth token
          localStorage.setItem('authToken', authToken);
          console.warn('No refresh token received from login');
        }

        navigate('/dashboard');
      } else {
        toast.error(data.message || 'Login Failed');
      }
    } catch (error) {
      toast.error('Network error, try again');
    } finally {
      setLoading(false); // Set loading back to false when the request is completed
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      {/* Background decoration */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
        <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF9C15]/5 rounded-full blur-3xl' />
      </div>

      <div className='relative flex items-center justify-center min-h-[calc(100vh-80px)] px-4'>
        <div className='w-full max-w-md'>
          {/* Login Card */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl'>
            {/* Header */}
            <div className='text-center mb-8'>
              <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FF9C15] to-[#F42026] rounded-2xl mb-4 shadow-lg'>
                <svg className='w-8 h-8 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' />
                </svg>
              </div>
              <h1 className='text-2xl font-bold text-white mb-2'>
                Welcome to{' '}
                <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
                  PingTop
                </span>
              </h1>
              <p className='text-gray-400 text-sm'>Sign in to access the moderation dashboard</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className='space-y-5'>
              <div>
                <label className='block text-sm font-medium text-gray-300 mb-2'>Username or Phone</label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <svg className='w-5 h-5 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
                    </svg>
                  </div>
                  <input
                    type='text'
                    name='PhoneOrUserName'
                    value={credentials.PhoneOrUserName}
                    placeholder='Enter your username'
                    className='w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all'
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-300 mb-2'>Password</label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <svg className='w-5 h-5 text-gray-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                    </svg>
                  </div>
                  <input
                    type='password'
                    name='Password'
                    value={credentials.Password}
                    placeholder='Enter your password'
                    className='w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all'
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Remember Me & Environment Toggle */}
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    id='rememberMe'
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className='w-4 h-4 rounded border-white/20 bg-white/10 text-[#FF9C15] focus:ring-[#FF9C15] focus:ring-offset-0 cursor-pointer'
                  />
                  <label htmlFor='rememberMe' className='ml-2 text-sm text-gray-300 cursor-pointer select-none'>
                    Remember me
                  </label>
                </div>

                {/* Environment Toggle */}
                <div className='flex items-center gap-2'>
                  <span className={`text-xs ${!isProd ? 'text-[#FF9C15]' : 'text-gray-500'}`}>Dev</span>
                  <button
                    type='button'
                    onClick={handleEnvToggle}
                    className={`relative w-10 h-5 rounded-full transition-colors ${isProd ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isProd ? 'translate-x-5' : ''}`}
                    />
                  </button>
                  <span className={`text-xs ${isProd ? 'text-green-400' : 'text-gray-500'}`}>Prod</span>
                </div>
              </div>

              <button
                type='submit'
                className='w-full py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] hover:from-[#e88c13] hover:to-[#d91c22] text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className='animate-spin h-5 w-5' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14 5l7 7m0 0l-7 7m7-7H3' />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className='mt-6 pt-6 border-t border-white/10 text-center'>
              <p className='text-xs text-gray-500'>
                Authorized personnel only
              </p>
            </div>
          </div>

          {/* Branding */}
          <div className='text-center mt-6'>
            <p className='text-sm text-gray-500'>
              <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent font-semibold'>PingTop</span>{' '}
              Moderation System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
